import { useRouter } from 'next/router';
import { getQueryParam } from '../helpers/get-query-param';

/**
 * Get value from query params
 * @deprecated prefer to use useStringQueryParams, useNumberQueryParams, useEnumQueryParams or useBooleanQueryParams instead
 * @param queryName string
 * @param fallbackValue T
 * @returns T
 * @example
 * const id = useQueryParams("id"); // id = string | undefined
 * const id = useQueryParams("id", "34"); // id = string | "34"
 * const color = useQueryParams<"red" | "blue">("color", "red"); // "red" | "blue"
 * const enum = useQueryParams<MyEnum>("enum", MyEnum.VALUE_1); // MyEnum
 */
export function useQueryParams<T = string>(
  queryName: string,
  fallbackValue?: T
) {
  const { query } = useRouter();
  return getQueryParam<T>(query?.[queryName] as T) || fallbackValue;
}

/**
 * Get an enum from query params
 * @param queryName string
 * @param fallbackValue T | undefined
 * @returns T | undefined
 * @example
 * const color = useEnumQueryParams<"red" | "blue">("color"); // "red" | "blue" | undefined
 * const enum = useEnumQueryParams<MyEnum>("enum"); // MyEnum | undefined
 */
export function useEnumQueryParams<T>(queryName: string): T | undefined;
/**
 * Get an enum from query params
 * @param queryName string
 * @param fallbackValue T
 * @returns T
 * @example
 * const color = useEnumQueryParams<"red" | "blue">("color", "red"); // "red" | "blue"
 * const enum = useEnumQueryParams<MyEnum>("enum", MyEnum.VALUE_1); // MyEnum
 */
export function useEnumQueryParams<T>(queryName: string, fallbackValue: T): T;
export function useEnumQueryParams<T>(queryName: string, fallbackValue?: T) {
  const { query } = useRouter();
  return getQueryParam<T>(query?.[queryName] as T) || fallbackValue;
}

/**
 * Get a string from query params
 * @param queryName string
 * @param fallbackValue string
 * @returns string | undefined
 * @example
 * const id = useStringQueryParams("id", "1"); // id = string | "1"
 * const id = useStringQueryParams("id"); // id = string | undefined
 */
export function useStringQueryParams(queryName: string): string | undefined;
/**
 * Get a string from query params
 * @param queryName string
 * @param fallbackValue string
 * @returns string
 * @example
 * const id = useStringQueryParams("id", "1"); // id = string | "1"
 */
export function useStringQueryParams(
  queryName: string,
  fallbackValue: string
): string;
export function useStringQueryParams(
  queryName: string,
  fallbackValue?: string
) {
  const { query } = useRouter();
  return getQueryParam(query?.[queryName]) || fallbackValue;
}

/**
 * Get a number from query params
 * @param queryName string
 * @param fallbackValue number
 * @returns number | undefined
 * @example
 * const page = useNumberQueryParams("page", 1); // page = number | 1
 * const page = useNumberQueryParams("page"); // page = number | undefined
 */
export function useNumberQueryParams(queryName: string): number | undefined;
/**
 * Get a number from query params
 * @param queryName string
 * @param fallbackValue number
 * @returns number
 * @example
 * const page = useNumberQueryParams("page", 1); // page = number | 1
 */
export function useNumberQueryParams(
  queryName: string,
  fallbackValue: number
): number;
export function useNumberQueryParams(
  queryName: string,
  fallbackValue?: number
) {
  const { query } = useRouter();
  return Number(getQueryParam(query?.[queryName])) || fallbackValue;
}

/**
 * Get a boolean from query params
 * @param queryName string
 * @param fallbackValue boolean
 * @returns boolean | undefined
 * @example
 * const isSaved = useBooleanQueryParams("isSaved", false); // isSaved = boolean | false
 * const isSaved = useBooleanQueryParams("isSaved"); // isSaved = boolean | undefined
 */
export function useBooleanQueryParams(queryName: string): boolean | undefined;
/**
 * Get a boolean from query params
 * @param queryName string
 * @param fallbackValue boolean
 * @returns boolean
 * @example
 * const isSaved = useBooleanQueryParams("isSaved", false); // isSaved = boolean | false
 */
export function useBooleanQueryParams(
  queryName: string,
  fallbackValue: boolean
): boolean;
export function useBooleanQueryParams(
  queryName: string,
  fallbackValue?: boolean
): boolean | undefined {
  const { query } = useRouter();
  const value = getQueryParam(query?.[queryName]);

  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return fallbackValue;
}

/**
 * Get an JSON object from query params (including arrays)
 * uses JSON.parse under the hood
 * @param queryName string
 * @param fallbackValue T | undefined
 * @returns T | undefined
 * @example
 * const userInfo = useJSONQueryParams<Record<string, string>>("userInfo"); // {"name": "John"} | undefined
 */
export function useJSONQueryParams<T>(queryName: string): T | undefined;
/**
 * Get an enum from query params
 * @param queryName string
 * @param fallbackValue T
 * @returns T
 * @example
 * const options = useJSONQueryParams<Record<string, number>>("options",{"size":45}); // {"size": 45}
 */
export function useJSONQueryParams<T>(queryName: string, fallbackValue: T): T;
export function useJSONQueryParams<T>(queryName: string, fallbackValue?: T) {
  const jsonString = useStringQueryParams(queryName);
  if (!jsonString) return fallbackValue;
  try {
    return (JSON.parse(jsonString) as T) || fallbackValue;
  } catch (e) {
    console.error(e);
    return fallbackValue;
  }
}
